import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Pencil, Trash2, Users, Search, DollarSign, Award, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Button, Modal, Field, Pill, EmptyState } from "@/components/kit";
import { formatDate, formatIDR } from "@/lib/format";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const todayISO = () => new Date().toISOString().slice(0, 10);
const keyOf = (name, date, time) => `${name}|${date}|${time}`;

export default function Classes() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const canManage = user.role === "owner" || (user.role === "admin" && user.all_access);
  const [scope, setScope] = useState("upcoming");
  const [q, setQ] = useState("");
  const [defs, setDefs] = useState(null);
  const [summary, setSummary] = useState([]);
  const [pending, setPending] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [roster, setRoster] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, sum, pend, c] = await Promise.all([
        api.classSchedule().catch(() => []),
        api.classesSummary().catch(() => []),
        api.classPendingPayments().catch(() => []),
        api.classCategories().catch(() => []),
      ]);
      setDefs(s); setSummary(sum); setPending(pend); setCats(c);
    } catch (e) { toast.error(e.message); setDefs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const joinedMap = useMemo(() => {
    const m = {};
    summary.forEach((s) => { m[keyOf(s.class_name, s.date, s.time)] = s.enrolled; });
    return m;
  }, [summary]);
  const pendingMap = useMemo(() => {
    const m = {};
    pending.forEach((p) => { const k = keyOf(p.class_name, p.date, p.time); m[k] = (m[k] || 0) + 1; });
    return m;
  }, [pending]);

  const visible = useMemo(() => {
    if (!defs) return [];
    const today = todayISO();
    const needle = q.trim().toLowerCase();
    let rows = defs.filter((d) => {
      const isPast = !!d.date && d.date < today;
      if (scope === "upcoming" && isPast) return false;
      if (scope === "past" && !isPast) return false;
      if (needle && !String(d.name).toLowerCase().includes(needle)) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      const da = a.next_date || a.date || "", db = b.next_date || b.date || "";
      if (da !== db) return scope === "past" ? (da < db ? 1 : -1) : (da < db ? -1 : 1);
      return (a.start || "") < (b.start || "") ? -1 : 1;
    });
    return rows;
  }, [defs, scope, q]);

  const remove = async (id) => {
    if (!window.confirm("Hapus jadwal kelas ini?")) return;
    try { await api.deleteClassSchedule(id); toast.success("Terhapus"); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <PageHeader eyebrow={isOwner ? "Owner" : "Admin"} title="Kelas"
        subtitle="Kelola jadwal kelas, peserta, dan validasi pembayaran/membership."
        right={canManage && <Button onClick={() => setShowAdd(true)} data-testid="add-class"><Plus className="h-4 w-4" /> Jadwal Baru</Button>}
      />

      <div className="mb-4 flex gap-2">
        {[["upcoming", "Akan Datang"], ["past", "Lampau"], ["all", "Semua"]].map(([m, l]) => (
          <Pill key={m} active={scope === m} onClick={() => setScope(m)} data-testid={`class-scope-${m}`}>{l}</Pill>
        ))}
      </div>
      <div className="relative mb-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
        <input className="hsc-input pl-9" placeholder="Cari nama kelas" value={q} onChange={(e) => setQ(e.target.value)} data-testid="class-search" />
      </div>

      {defs === null ? <Loading /> : !visible.length ? (
        <EmptyState icon={Users} title="Tidak ada kelas" subtitle={canManage ? "Tambahkan jadwal kelas." : "Belum ada kelas."} />
      ) : (
        <div className="space-y-3">
          {visible.map((d) => {
            const nd = d.next_date || d.date;
            const joined = joinedMap[keyOf(d.name, nd, d.start)] || 0;
            const pend = pendingMap[keyOf(d.name, nd, d.start)] || 0;
            const isPast = !!d.date && d.date < todayISO();
            return (
              <Card key={d.id} className="flex items-center justify-between gap-3" data-testid={`class-def-${d.id}`}>
                <button className="min-w-0 flex-1 text-left" onClick={() => setRoster(d)} data-testid={`class-open-${d.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-white">{d.name}</span>
                    <span className="text-[10px] uppercase tracking-widest text-[#6E6E73]">{d.category}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isPast ? "bg-[#1F1F22] text-[#8E8E93]" : "bg-[#0d2a1a] text-[#7CD992]"}`}>{isPast ? "Selesai" : "Akan Datang"}</span>
                    {pend > 0 && <span className="rounded-full bg-[#2a230d] px-2 py-0.5 text-[10px] font-semibold text-[#E7C25B]" data-testid={`class-pending-${d.id}`}>{pend} belum bayar</span>}
                  </div>
                  <div className="mt-1 text-[12px] text-[#8E8E93]">{nd ? formatDate(nd) : d.day} · {d.time} · {joined}/{d.capacity || "-"} peserta</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[#5A5A5F]"><Users className="h-3 w-3" /> Kelola peserta <ChevronRight className="h-3 w-3" /></div>
                </button>
                {canManage && (
                  <div className="flex shrink-0 gap-1">
                    <button className="rounded-lg p-2 text-[#8E8E93] hover:bg-[#141416] hover:text-white" onClick={() => setEdit(d)} data-testid={`class-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
                    <button className="rounded-lg p-2 text-[#8E8E93] hover:bg-[#1a0d0d] hover:text-[#FF6B6B]" onClick={() => remove(d.id)} data-testid={`class-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {(showAdd || edit) && (
        <ClassModal cats={cats} existing={edit} onClose={() => { setShowAdd(false); setEdit(null); }}
          onSaved={() => { setShowAdd(false); setEdit(null); load(); }} />
      )}
      {roster && <RosterModal def={roster} onClose={() => setRoster(null)} onChanged={load} />}
    </div>
  );
}

function RosterModal({ def, onClose, onChanged }) {
  const nd = def.next_date || def.date;
  const [list, setList] = useState(null);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");

  const refresh = useCallback(async () => {
    const [r, ms] = await Promise.all([
      api.classRoster(def.name, nd, def.start).catch(() => []),
      api.ownerMembers().catch(() => []),
    ]);
    setList(r); setMembers(ms);
  }, [def, nd]);
  useEffect(() => { refresh(); }, [refresh]);

  const done = () => { onChanged && onChanged(); };

  const markPaid = async () => {
    const amt = parseInt(String(payAmount).replace(/[^0-9]/g, ""), 10) || 0;
    if (amt <= 0) return toast.error("Masukkan nominal valid");
    setBusy(`pay-${payFor.member_id}`);
    try {
      const res = await api.classMarkPaid({ member_id: payFor.member_id, class_name: def.name, date: nd, time: def.start, amount: amt, category: def.category });
      toast.success(`${payFor.member_name} LUNAS · +${res.points_earned ?? 0} poin`);
      setPayFor(null); setPayAmount(""); await refresh(); done();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const confirmMembership = async (p) => {
    setBusy(`ms-${p.member_id}`);
    try {
      await api.classConfirmMembership({ member_id: p.member_id, class_name: def.name, date: nd, time: def.start, category: def.category });
      toast.success(`${p.member_name} · via Membership`); await refresh(); done();
    } catch (e) { toast.error(e.message || "Gagal validasi membership"); } finally { setBusy(null); }
  };
  const removeP = async (mid) => {
    setBusy(`rm-${mid}`);
    try { await api.unenrollClass({ member_id: mid, class_name: def.name, date: nd, time: def.start }); await refresh(); done(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const addP = async (m) => {
    setBusy(`add-${m.id}`);
    try {
      const res = await api.enrollClass({ member_id: m.id, class_name: def.name, category: def.category, date: nd, time: def.start });
      toast.success(res.already ? `${m.name} sudah terdaftar` : `Ditambah: ${m.name}`);
      setSearch(""); await refresh(); done();
    } catch (e) { toast.error(e.message === "Class is full" ? "Kelas penuh" : e.message); } finally { setBusy(null); }
  };

  const candidates = search.trim()
    ? members.filter((m) => !(list || []).some((r) => r.member_id === m.id) && (m.name || "").toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];

  return (
    <Modal open title={def.name} onClose={onClose} maxWidth="max-w-lg">
      <div className="mb-3 text-[12px] text-[#8E8E93]">{nd ? formatDate(nd) : def.day} · {def.time} · kapasitas {def.capacity}</div>

      <div className="mb-2 hsc-label">Peserta ({list ? list.length : 0})</div>
      {list === null ? <Loading /> : !list.length ? (
        <div className="py-3 text-sm text-[#6E6E73]">Belum ada peserta.</div>
      ) : (
        <div className="space-y-2" data-testid="roster-list">
          {list.map((p) => (
            <div key={p.member_id} className="flex items-center gap-2 rounded-xl border border-[#1F1F22] bg-[#0E0E10] p-3" data-testid={`roster-item-${p.member_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white">{p.member_name}</div>
                {p.payment_status === "paid" ? <div className="text-[10px] font-bold tracking-wider text-[#8FD3E8]">LUNAS{p.source === "transaction" ? " · TRANSAKSI" : ""}</div>
                  : p.payment_status === "membership" ? <div className="text-[10px] font-bold tracking-wider text-[#B39DDB]">MEMBERSHIP</div>
                  : <div className="text-[10px] font-bold tracking-wider text-[#E7C25B]">BELUM BAYAR</div>}
              </div>
              {p.payment_status !== "paid" && p.payment_status !== "membership" && (
                <>
                  <button className="flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[11px] font-bold text-black" onClick={() => { setPayFor(p); setPayAmount(""); }} data-testid={`roster-pay-${p.member_id}`}><DollarSign className="h-3 w-3" /> Bayar</button>
                  <button className="rounded-full border border-[#3A3A3C] p-2 text-[#D1D1D6] hover:border-white" disabled={busy === `ms-${p.member_id}`} onClick={() => confirmMembership(p)} data-testid={`roster-membership-${p.member_id}`}><Award className="h-4 w-4" /></button>
                </>
              )}
              <button className="rounded-full border border-[#3A3A3C] p-2 text-[#8E8E93] hover:text-[#FF8080]" disabled={busy === `rm-${p.member_id}`} onClick={() => removeP(p.member_id)} data-testid={`roster-remove-${p.member_id}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 hsc-label mb-2">Tambah Peserta</div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
        <input className="hsc-input pl-9" placeholder="Cari member" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="roster-search" />
      </div>
      {candidates.map((m) => (
        <button key={m.id} className="mt-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-[#141416]" disabled={busy === `add-${m.id}`} onClick={() => addP(m)} data-testid={`roster-add-${m.id}`}>
          <span className="text-white">{m.name}</span><Plus className="h-4 w-4 text-[#8E8E93]" />
        </button>
      ))}

      {payFor && (
        <Modal open title="Tandai Bayar" onClose={() => setPayFor(null)} maxWidth="max-w-sm">
          <div className="space-y-4">
            <div className="text-sm text-[#8E8E93]">{payFor.member_name} · {def.name}</div>
            <Field label="Nominal (Rp)">
              <input type="number" className="hsc-input" value={payAmount} autoFocus placeholder="mis. 150000"
                onChange={(e) => setPayAmount(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") markPaid(); }} data-testid="roster-pay-amount" />
              {parseInt(String(payAmount).replace(/[^0-9]/g, ""), 10) > 0 && <div className="mt-1 text-xs text-[#6E6E73]">{formatIDR(parseInt(String(payAmount).replace(/[^0-9]/g, ""), 10))}</div>}
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPayFor(null)}>Batal</Button>
              <Button disabled={busy === `pay-${payFor.member_id}`} onClick={markPaid} data-testid="roster-pay-submit">Simpan & Catat</Button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

function ClassModal({ cats, existing, onClose, onSaved }) {
  const [f, setF] = useState({
    name: existing?.name || "", category: existing?.category || cats[0] || "Hyrox",
    mode: existing?.date ? "date" : "day", day: existing?.day || "Senin", date: existing?.date || "",
    time: existing?.time || "18.00-19.00", capacity: existing?.capacity || 16, group: existing?.group || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Nama kelas wajib diisi");
    setBusy(true);
    try {
      const body = { name: f.name.trim(), category: f.category, time: f.time, capacity: Number(f.capacity), group: f.group || undefined,
        ...(f.mode === "date" ? { date: f.date } : { day: f.day }) };
      if (existing) await api.updateClassSchedule(existing.id, body, true);
      else await api.addClassSchedule(body, true);
      toast.success("Tersimpan"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open title={existing ? "Edit Jadwal" : "Jadwal Kelas Baru"} onClose={onClose}>
      <div className="space-y-4">
        <Field label="Nama Kelas"><input className="hsc-input" value={f.name} onChange={set("name")} data-testid="class-name" /></Field>
        <Field label="Kategori (pilih atau ketik baru)">
          <input className="hsc-input" list="class-cat-options" value={f.category} onChange={set("category")} placeholder="mis. Calisthenic" data-testid="class-category" />
          <datalist id="class-cat-options">
            {(cats.length ? cats : ["Hyrox", "Boxing", "Barre", "Strength & Conditioning"]).map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <div className="flex gap-2">
          <Pill active={f.mode === "day"} onClick={() => setF((s) => ({ ...s, mode: "day" }))}>Mingguan</Pill>
          <Pill active={f.mode === "date"} onClick={() => setF((s) => ({ ...s, mode: "date" }))}>Tanggal spesifik</Pill>
        </div>
        {f.mode === "day" ? (
          <Field label="Hari"><select className="hsc-input" value={f.day} onChange={set("day")}>{DAYS.map((d) => <option key={d}>{d}</option>)}</select></Field>
        ) : (
          <Field label="Tanggal"><input type="date" className="hsc-input" value={f.date} onChange={set("date")} /></Field>
        )}
        <Field label="Waktu (mis. 18.00-19.00)"><input className="hsc-input" value={f.time} onChange={set("time")} /></Field>
        <Field label="Kapasitas"><input type="number" className="hsc-input" value={f.capacity} onChange={set("capacity")} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy} data-testid="class-save">Simpan</Button>
        </div>
      </div>
    </Modal>
  );
}
